import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import uniqBy from "lodash/uniqBy";
import { useMemo, useState } from "react";
import {
  AccountToOrganizationMembershipStatus,
  GetUserComboboxOptionsQueryQuery,
  GetUserComboboxOptionsQueryQueryVariables,
  UserComboboxUserOptionFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import cache, { currentOrganizationVar, currentUserVar } from "@cache/cache";
import useDebounce from "@components/use-debounce/use-debounce";
import {
  UserComboboxDirectReportsOption,
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxOrgOption,
  UserComboboxReportingTreeOption,
  UserComboboxTeamOption,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { delay, graphqlNone } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import getUserComboboxOptionsQuery, {
  UserComboboxTeamOptionFragment,
  UserComboboxUserOptionFragment as UserComboboxUserOptionGQLFragment,
} from "../people-dropdown/get-user-combobox-options-query";

export const getCacheTeamOption = (teamId: number) => {
  const team = cache.readFragment<UserComboboxTeamOption>({
    id: cache.identify({ __typename: "TeamNode", id: teamId }),
    fragment: UserComboboxTeamOptionFragment,
  });
  return team;
};

const getCachedUser = (
  u: BasicUser,
  showActiveAssessmentNominationCount: boolean,
) => {
  return cache.readFragment<UserComboboxUserOptionFragment>({
    id: `UserNode:${u.id}`,
    variables: { showActiveAssessmentNominationCount },
    fragment: UserComboboxUserOptionGQLFragment,
  });
};

const getCachedUsers = (
  users: BasicUser[],
  showActiveAssessmentNominationCount: boolean,
): UserComboboxUserOptionFragment[] => {
  return compact(
    users.map((u) => getCachedUser(u, showActiveAssessmentNominationCount)),
  );
};

const useUserComboboxQuery = ({
  queryOptions,
  selected,
  emptyOption,
  excludeUserIds = [],
  excludeTeamIds = [],
  includeDeactivated = false,
  types = [UserComboboxOptionType.USER],
  fetchOutsideCurrentOrganization = false,
  organizationId,
  canLead = false,
  showActiveAssessmentNominationCount = false,
}: {
  queryOptions?: {
    skip?: boolean;
  };
  emptyOption?: UserComboboxOption;
  selected?: UserComboboxOption | null;
  excludeUserIds?: number[];
  excludeTeamIds?: number[];
  includeDeactivated?: boolean;
  types?: UserComboboxOptionType[];
  fetchOutsideCurrentOrganization?: boolean;
  // Search this organization instead of the current one.
  organizationId?: number;
  // Only people who can lead a department there.
  canLead?: boolean;
  showActiveAssessmentNominationCount?: boolean;
}) => {
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const searchedOrganizationId = organizationId ?? currentOrganization.id;
  const isOtherOrganization = searchedOrganizationId !== currentOrganization.id;

  if (selected && !types.includes(selected.type)) {
    throw new Error(`UserComboBox selected type not allowed ${selected.type}`);
  }

  const initialOptions: UserComboboxOption[] = useMemo(() => {
    // The current user's people belong to the current organization, and
    // the server hasn't checked they can lead.
    if (isOtherOrganization || canLead) {
      return compact([selected, emptyOption]);
    }
    const managers: UserComboboxOption[] =
      currentUser && types.includes(UserComboboxOptionType.USER)
        ? getCachedUsers(
            assertEdgesNonNull(currentUser.managers),
            showActiveAssessmentNominationCount,
          ).map((u) => ({
            type: UserComboboxOptionType.USER,
            ...u,
          }))
        : [];
    const directReports: UserComboboxOption[] =
      currentUser && types.includes(UserComboboxOptionType.USER)
        ? getCachedUsers(
            assertEdgesNonNull(currentUser.directReports),
            showActiveAssessmentNominationCount,
          ).map((u) => ({
            type: UserComboboxOptionType.USER,
            ...u,
          }))
        : [];
    const cachedCurrentUser = getCachedUser(
      currentUser,
      showActiveAssessmentNominationCount,
    );
    const currentUserOption: UserComboboxOption = {
      type: UserComboboxOptionType.USER,
      ...(cachedCurrentUser ? cachedCurrentUser : currentUser),
    };
    const favouriteGroups =
      currentUser?.favouritesGroups &&
      types.includes(UserComboboxOptionType.USER)
        ? assertEdgesNonNull(currentUser.favouritesGroups)
        : [];
    const favouriteUsers = favouriteGroups.reduce((memo, favouriteGroup) => {
      return [
        ...memo,
        ...getCachedUsers(
          assertEdgesNonNull(favouriteGroup.users),
          showActiveAssessmentNominationCount,
        ),
      ];
    }, [] as BasicUser[]);
    const favouriteUserOptions: UserComboboxOption[] = favouriteUsers.map(
      (u) => ({ type: UserComboboxOptionType.USER, ...u }),
    );

    const teams: UserComboboxTeamOption[] = types.includes(
      UserComboboxOptionType.TEAM,
    )
      ? assertEdgesNonNull(currentUser.teams).map((team) => ({
          type: UserComboboxOptionType.TEAM,
          ...team,
        }))
      : [];
    const org: UserComboboxOrgOption = {
      type: UserComboboxOptionType.ORG,
      ...currentOrganization,
    };
    const directReportsOption: UserComboboxDirectReportsOption = {
      type: UserComboboxOptionType.DIRECT_REPORTS,
      id: UserComboboxOptionType.DIRECT_REPORTS,
    };
    const reportingTreeOption: UserComboboxReportingTreeOption = {
      type: UserComboboxOptionType.REPORTING_TREE,
      id: UserComboboxOptionType.REPORTING_TREE,
    };
    return uniqBy(
      compact([
        selected,
        emptyOption,
        types.includes(UserComboboxOptionType.USER) && currentUserOption,
        types.includes(UserComboboxOptionType.ORG) && org,
        ...managers,
        ...directReports,
        ...favouriteUserOptions,
        ...teams,
        types.includes(UserComboboxOptionType.DIRECT_REPORTS) &&
          directReportsOption,
        types.includes(UserComboboxOptionType.REPORTING_TREE) &&
          reportingTreeOption,
      ]),
      (opt) => `${opt.type}${opt.id}`,
    );
  }, [
    canLead,
    currentOrganization,
    currentUser,
    emptyOption,
    isOtherOrganization,
    selected,
    showActiveAssessmentNominationCount,
    types,
  ]);
  const userInitialOptions = initialOptions.filter(
    (option) => option.type === UserComboboxOptionType.USER,
  );
  const [query, setQuery] = useState("");
  const [_options, setOptions] = useState<UserComboboxOption[]>(initialOptions);
  const debouncedQuery = useDebounce(query, delay.searchDebounce);

  // HOOKS
  const { loading } = useQuery<
    GetUserComboboxOptionsQueryQuery,
    GetUserComboboxOptionsQueryQueryVariables
  >(getUserComboboxOptionsQuery, {
    notifyOnNetworkStatusChange: true,
    skip: queryOptions?.skip,
    variables: {
      showActiveAssessmentNominationCount,
      organizationId: fetchOutsideCurrentOrganization
        ? null
        : searchedOrganizationId,
      userId:
        selected?.type === UserComboboxOptionType.USER
          ? selected.id
          : graphqlNone,
      hasUserId:
        selected?.type === UserComboboxOptionType.USER &&
        !!selected.id &&
        !debouncedQuery,
      teamId:
        selected?.type === UserComboboxOptionType.TEAM
          ? selected.id
          : graphqlNone,
      hasTeamId:
        selected?.type === UserComboboxOptionType.TEAM &&
        !!selected.id &&
        !debouncedQuery,
      search: debouncedQuery,
      excludeUserIds,
      excludeTeamIds,
      // if no queries, we try to show a max of 10 initial user options
      limitUser: query ? 10 : Math.max(0, 10 - userInitialOptions.length),
      includeDeactivated: !!debouncedQuery && includeDeactivated,
      canLead,
    },
    onCompleted: (data) => {
      const selectedUser: UserComboboxUserOption | null = data?.user
        ? {
            type: UserComboboxOptionType.USER,
            deactivated:
              data.user.membershipStatus ===
              AccountToOrganizationMembershipStatus.Inactive,
            ...data.user,
          }
        : null;
      const users: UserComboboxUserOption[] =
        data?.users && types.includes(UserComboboxOptionType.USER)
          ? assertEdgesNonNull(data.users).map((u) => ({
              type: UserComboboxOptionType.USER,
              deactivated:
                u.membershipStatus ===
                AccountToOrganizationMembershipStatus.Inactive,
              ...u,
            }))
          : [];
      const selectedTeam: UserComboboxTeamOption | null = data?.team
        ? { type: UserComboboxOptionType.TEAM, ...data.team }
        : null;
      const teams: UserComboboxTeamOption[] =
        data?.teams && types.includes(UserComboboxOptionType.TEAM)
          ? assertEdgesNonNull(data.teams).map((t) => ({
              type: UserComboboxOptionType.TEAM,
              ...t,
            }))
          : [];

      const newOptions = uniqBy(
        compact([
          selectedUser,
          selectedTeam,
          ...users,
          ...teams,
          ...options,
          ...initialOptions,
        ]),
        (opt) => `${opt.type}${opt.id}`,
      );
      setOptions(newOptions);
    },
  });

  const optionsWithoutExcludedUsers = _options.filter(
    (opt) =>
      opt.type === UserComboboxOptionType.ORG ||
      opt.type === UserComboboxOptionType.DIRECT_REPORTS ||
      opt.type === UserComboboxOptionType.REPORTING_TREE ||
      (opt.type === UserComboboxOptionType.USER &&
        !excludeUserIds.includes(opt.id)) ||
      (opt.type === UserComboboxOptionType.TEAM &&
        !excludeTeamIds.includes(opt.id)),
  );
  const validOptions = optionsWithoutExcludedUsers.filter(({ type }) =>
    types.includes(type),
  );

  const options = validOptions;

  return {
    options,
    loading: loading || query !== debouncedQuery,
    query,
    setQuery,
  };
};

export default useUserComboboxQuery;
